import { useRuntimeConfig } from '#app';
import { ref } from "vue";
import type { Ref } from "vue";
import type {
  CheckoutOptions,
  PaymentSessionResponse,
  ProviderEnvironment,
} from "../types/index";

// ── Auth Cookie Helper ─────────────────────────────────────────────────────

/**
 * Reads the better-auth session cookie from localStorage.
 * The cookie is stored as a JSON object mapping cookie name → { value, expires }.
 * Falls back to the raw string if parsing fails.
 */
export function getAuthCookie(): string {
  if (typeof localStorage === 'undefined') return '';
  const raw = localStorage.getItem('better-auth_cookie');
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw) as Record<
      string,
      { value?: string; expires?: string | null }
    >;
    return Object.entries(parsed)
      .map(([key, val]) => (val?.value ? `${key}=${val.value}` : ''))
      .filter(Boolean)
      .join('; ');
  } catch {
    // Not JSON — treat as a raw cookie string
    return raw;
  }
}

// ── Factory Pattern ────────────────────────────────────────────────────────

export interface CheckoutFactoryOptions {
  orderId: string;
  returnUrl?: string;
  onPaymentCompleted: (resultCode: string) => void;
  onPaymentFailed: (resultCode: string) => void;
  onError?: (error: Error) => void;
}

export interface CheckoutFactoryConfig {
  resolveLocale: () => string;
  /** Derive provider-compatible country code (ISO 3166-1 alpha-2) from user/browser context */
  resolveCountryCode: () => string;
  resolveAuthCookie: () => string;
  resolveReturnUrl: (providedUrl: string | undefined) => string;
}

/**
 * Factory that produces a project-specific `useCheckout` composable.
 *
 * Each Nuxt app (client, console, website) calls this once with
 * project-specific resolvers for locale, country, auth cookie, and return URL.
 * The returned function is a thin wrapper around the core `useCheckout` that
 * reads `useRuntimeConfig()` at call-time to obtain the Convex site URL and
 * provider environment.
 */
export function createUseCheckout(config: CheckoutFactoryConfig) {
  return function useCheckoutFactory(options: CheckoutFactoryOptions) {
    const runtimeConfig = useRuntimeConfig();
    const convexSiteUrl = runtimeConfig.public.convexSiteUrl as string;
    // TODO: rename runtimeConfig key from adyenEnvironment to paymentEnvironment
    const environment = (runtimeConfig.public.adyenEnvironment ??
      'test') as ProviderEnvironment;

    return useCheckout({
      convexSiteUrl,
      authCookie: config.resolveAuthCookie(),
      orderId: options.orderId,
      returnUrl: config.resolveReturnUrl(options.returnUrl),
      environment,
      countryCode: config.resolveCountryCode(),
      locale: config.resolveLocale(),
      onPaymentCompleted: options.onPaymentCompleted,
      onPaymentFailed: options.onPaymentFailed,
      onError: options.onError,
    });
  };
}

// ── Core Composable ────────────────────────────────────────────────────────

/**
 * Provider-agnostic checkout composable.
 *
 * Creates a payment session via the Convex HTTP endpoint, detects the provider
 * from the session response, and dynamically loads the appropriate provider SDK
 * (Adyen Drop-in, Stripe Elements, etc.).
 *
 * Works identically across website, client (Capacitor), and console (Capacitor).
 *
 * Usage:
 * ```ts
 * const { initCheckout, destroy, loading, error } = useCheckout({
 *   convexSiteUrl: 'https://your-deployment.eu-west-1.convex.site',
 *   authCookie: 'session-cookie-value',
 *   orderId: 'order-id',
 *   returnUrl: 'https://your-site.com/checkout/result',
 *   environment: 'test',
 *   onPaymentCompleted: (resultCode) => { ... },
 *   onPaymentFailed: (resultCode) => { ... },
 * });
 *
 * // Mount the checkout to a DOM element
 * await initCheckout('#checkout-container');
 *
 * // Clean up when component unmounts
 * destroy();
 * ```
 */
export function useCheckout(options: CheckoutOptions) {
  const loading: Ref<boolean> = ref(true);
  const error: Ref<string | null> = ref(null);
  let cleanup: (() => void) | null = null;

  /**
   * Fetch a payment session from the backend.
   */
  async function fetchSession(): Promise<PaymentSessionResponse> {
    const response = await fetch(
      `${options.convexSiteUrl}/payments/sessions`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Better-Auth-Cookie": options.authCookie,
        },
        body: JSON.stringify({
          orderId: options.orderId,
          returnUrl: options.returnUrl,
          shopperReference: options.shopperReference,
        }),
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP ${response.status}: Failed to create payment session`,
      );
    }

    return response.json();
  }

  /**
   * Initialize the checkout and mount the provider's UI component.
   *
   * @param containerSelector - CSS selector or HTMLElement to mount the checkout to
   */
  async function initCheckout(
    containerSelector: string | HTMLElement,
  ): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      // 1. Create a payment session via the backend
      const session = await fetchSession();

      if (!session.success || !session.externalSessionId || !session.sessionToken || !session.clientKey) {
        throw new Error(session.error || "Failed to create payment session");
      }

      // 2. Resolve the container element
      const container =
        typeof containerSelector === "string"
          ? document.querySelector<HTMLElement>(containerSelector)
          : containerSelector;

      if (!container) {
        throw new Error(
          `Checkout container not found: ${containerSelector}`,
        );
      }

      // 3. Dispatch to the appropriate provider's SDK
      const provider = session.provider ?? "adyen";

      if (provider === "adyen") {
        const { mountAdyenDropin } = await import("../providers/adyen/checkout");
        const instance = await mountAdyenDropin(container, session, options);
        cleanup = instance.destroy;
      } else {
        // Phase 8: else if (provider === 'stripe') { ... }
        throw new Error(`Payment provider '${provider}' is not yet supported`);
      }

      loading.value = false;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unknown error initializing checkout";
      error.value = message;
      loading.value = false;
      options.onError?.(err instanceof Error ? err : new Error(message));
    }
  }

  /**
   * Destroy the checkout UI and clean up resources.
   * Call this in the component's onBeforeUnmount lifecycle hook.
   */
  function destroy(): void {
    cleanup?.();
    cleanup = null;
  }

  return {
    initCheckout,
    destroy,
    loading,
    error,
  };
}
